import { supabaseClient } from "@/lib/supabase";

/**
 * Private bucket for every `file` input answer, on any form, at {diary_id}/{entry_id}/{input_id}/{file}.
 * Read access goes through diary access (signed URLs). The bucket id is legacy - it was first used for
 * prescriptions - and is kept because renaming a bucket means moving every stored patient file.
 */
export const INPUT_UPLOADS_BUCKET = "prescription-documents";

/**
 * Stored in react-hook-form and entry_values after upload.
 * - objectId: FK to storage.objects (entry_values.object_id)
 * - storagePath: full path within the bucket (for signed URL retrieval)
 * - fileName: original filename (entry_values.text_value)
 */
export interface DocumentUploadValue {
    objectId: string;
    storagePath: string;
    fileName: string;
}

export interface UploadDocumentParams {
    file: File;
    diaryId: string;
    entryId: string;
    inputId: string;
}

/** Sanitise filename for storage paths. */
export function sanitizeDocumentFilename(filename: string): string {
    const base = filename.split(/[/\\]/).pop() ?? "document";
    const sanitized = base.replace(/[^a-zA-Z0-9._-]/g, "_");
    return sanitized.length > 0 ? sanitized : "document";
}

/** Build storage path: {diary_id}/{entry_id}/{input_id}/{filename} */
export function buildDocumentStoragePath(params: {
    diaryId: string;
    entryId: string;
    inputId: string;
    filename: string;
}): string {
    const { diaryId, entryId, inputId, filename } = params;
    return `${diaryId}/${entryId}/${inputId}/${sanitizeDocumentFilename(filename)}`;
}

/**
 * Upload a document to Supabase Storage and return references for entry_values persistence.
 * Uses entry_values.object_id (storage.objects FK) plus text_value for the display filename.
 */
export async function uploadDocument(params: UploadDocumentParams): Promise<DocumentUploadValue> {
    const { file, diaryId, entryId, inputId } = params;
    const storagePath = buildDocumentStoragePath({
        diaryId,
        entryId,
        inputId,
        filename: file.name,
    });

    const { data, error } = await supabaseClient.storage.from(INPUT_UPLOADS_BUCKET).upload(storagePath, file, {
        cacheControl: "3600",
        upsert: true,
    });

    if (error) {
        throw error;
    }

    if (!data?.id) {
        throw new Error("Upload succeeded but no storage object id was returned.");
    }

    return {
        objectId: data.id,
        storagePath: data.path,
        fileName: file.name,
    };
}

/** Convert a camera capture data URL to a File for upload. */
export function dataUrlToFile(dataUrl: string, filename: string): File {
    const [header, base64] = dataUrl.split(",");
    const mime = header?.match(/:(.*?);/)?.[1] ?? "image/jpeg";
    const binary = atob(base64 ?? "");
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return new File([bytes], filename, { type: mime });
}
